import StatusPopover from '../../../components/v2/StatusPopover.jsx';
import { Link } from 'react-router-dom';
import FilterPopover from '../../../components/v2/FilterPopover.jsx';
import { campusLocalToMs } from '../../../libs.jsx';
import { relativeTimeFromCampusLocal } from '../../../utils/campusTime.js';
import AlertBellPopover from '../../../components/v2/AlertBellPopover.jsx';
import RowActionsMenu from '../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../components/v2/SortableTh.jsx';

/* sp_dev_getRepeaterByCampus LEFT JOINs the config table, so a device whose
   inovonics_recievers_repeaters_config row is missing (or only deprecated) comes
   back with no inovonic_system_id. The local server can't run it like that. */
export const isConfigMissing = (row) => row?.inovonic_system_id == null;

/* Only point at the menu item for roles that get it (SystemRowActionsMenu's canRepair). */
const configMissingMessage = (canRepair) =>
  'This device has no repeater configuration, so the server may not use it. ' +
  (canRepair
    ? 'Use Fix Missing Config in the row menu to recreate it.'
    : 'Contact PalCare support to have it restored.');

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

/**
 * Column definitions for the system devices (repeaters) table (7 columns).
 */
export const SYSTEM_COLUMN_DEFS = [
  { key: 'device_name', label: 'Name', sortable: true, filterable: true },
  { key: 'model_name', label: 'Model', sortable: true, filterable: true },
  { key: 'manufacture_uuid', label: 'Serial Number', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'network_id', label: 'Network', sortable: true, filterable: true },
  { key: 'last_active', label: 'Last Active', sortable: true, filterable: false },
  { key: 'mac_addr', label: 'Last Known Server', sortable: true, filterable: true },
  { key: 'is_active', label: 'Active Status', sortable: true, filterable: true },
];

/**
 * SystemDevicesTable — full data table for system devices (repeaters) with
 * sorting, filtering, sticky header, row selection, and row actions.
 */
const SystemDevicesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onDeleteDevice,
  onRepairConfig,
  editRights,
  deleteRights,
  tzOffset = 0,
  /* Map<deviceIdString, alert[]> from alertsByDevice(). A Map rather than a filter
     callback so the table does one pass over the alerts, not one per row. */
  alertsByDeviceId,
}) => {
  /* Ids compare as strings: device_id arrives as a number on the row while the alert
     ids are normalized to strings. */
  const alarmsFor = (deviceId) => alertsByDeviceId?.get(String(deviceId)) || null;
  const activeColumns = SYSTEM_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'device_name': {
        /* The marker rides the name column rather than getting one of its own:
           it is where the eye lands, and an extra column would cost width on a
           table that already scrolls sideways. */
        const alarms = alarmsFor(row.device_id);
        return (
          <>
            {alarms ? (
              <AlertBellPopover alerts={alarms} tzOffset={tzOffset} scope="device" />
            ) : null}
            {(
            <span
              className="dp-cell-text"
              style={{ fontWeight: 'var(--font-weight-semibold)' }}
            >
              {row.device_name || '\u2014'}
            </span>
          )}
            {alarms ? (
              <span className="pp-alarm-chip">
                <span className="pp-alarm-chip__dot" aria-hidden="true" />
                {alarms.length > 1 ? `${alarms.length} in alarm` : 'In alarm'}
              </span>
            ) : null}
            {isConfigMissing(row) ? (
              <StatusPopover message={configMissingMessage(!!(editRights && onRepairConfig))}>
                <span className="dp-config-chip has-popover">Config missing</span>
              </StatusPopover>
            ) : null}
          </>
        );
      }

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'last_active':
        return row.last_active ? (
          <StatusPopover message={fullDateTime(row.last_active)}>
            <span className={`dp-cell-text dp-cell-text--secondary${row?.last_active ? " has-popover" : ""}`}>
              {relativeTimeFromCampusLocal(row.last_active, tzOffset)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'is_active': {
        if (!row.is_active) {
          return (
            <span className="dp-status dp-status--inactive">
              <span className="dp-status__dot" aria-hidden="true" />
              Unassigned
            </span>
          );
        }
        const stale = row.last_active
          ? (Date.now() - campusLocalToMs(row.last_active, tzOffset)) > 30 * 3600000
          : true;
        return (
          <StatusPopover message={stale ? 'Device may be stale \u2014 test the device to ensure functionality' : undefined}>
            <span className={`dp-status ${stale ? 'dp-status--warning' : 'dp-status--active'}`}>
              <span className="dp-status__dot" aria-hidden="true" />
              Active
            </span>
          </StatusPopover>
        );
      }

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all devices"
                />
              </th>
            ) : null}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.device_id);
            return (
              <tr
                key={row.device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}${alarmsFor(row.device_id) ? ' has-alarm' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.device_name}`}
                    />
                  </td>
                ) : null}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <SystemRowActionsMenu
                    device={row}
                    editRights={editRights}
                    deleteRights={deleteRights}
                    onDeleteDevice={onDeleteDevice}
                    onRepairConfig={onRepairConfig}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * SystemRowActionsMenu — kebab menu for per-row system device actions.
 * Delete (no unassign for system devices), plus Fix Missing Config on a row
 * whose config row is missing.
 */
const SystemRowActionsMenu = ({
  device,
  editRights,
  deleteRights,
  onDeleteDevice,
  onRepairConfig,
}) => {
  const canRepair = editRights && onRepairConfig && isConfigMissing(device);
  if (!deleteRights && !canRepair) {
    return null;
  }

  return (
    <RowActionsMenu label={`Actions for ${device.device_name}`} classPrefix="dp">
      {(close) => (
        <>
          {canRepair ? (
            <button
              type="button"
              onClick={() => {
                onRepairConfig(device);
                close();
              }}
              className="dp-row-actions__item"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
                />
              </svg>
              Fix Missing Config
            </button>
          ) : null}
          {deleteRights ? (
            <button
              type="button"
              onClick={() => {
                onDeleteDevice(device);
                close();
              }}
              className="dp-row-actions__item dp-row-actions__item--danger"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              Delete Device
            </button>
          ) : null}
        </>
      )}
    </RowActionsMenu>
  );
};

export default SystemDevicesTable;
